import React, { useEffect, useState } from "react";
import { Box, Button, Grid, IconButton, Tab, Tabs } from "@mui/material";
import ReadMoreLess from "../ReadMoreLess";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ValidationSummaryDataGrid from "../DataGrids/ValidationSummaryDataGrid";
import ResourceSupportDocuments from "../Organisms/ResourceSupportDocuments";
import AdhocQueryDashboardTab from "./AdhocQueryDashboardTab";
import Incidents from "../Rules/Incidents";
import Loader from "../Molecules/Loader/Loader";
import ValidationResultHeader from "../Molecules/Resource/ValidationResultHeader";
import ValidationResultResourceStatistics from "../Molecules/Resource/ValidationResultResourceStatistics";
import ValidationResultParameters from "../Molecules/Resource/ValidationResultParameters";
import ValidationResultVariables from "./ValidationResultVariables";
import DuplicateRecords from "./DuplicateRecords";
import NullKeyErrors from "./NullKeyErrors";
import useFetchUsersList from "../../hooks/useFetchUsersList";
import { useAuth } from "../../contexts/AuthContext";
import { IDetailedValidationResult } from "../../types/Validation.type";
import { formatNumberWithCommas } from "../../services/utils";
import { keyframes } from "@mui/system";
import {
  LOW_CHIP_COLOR,
  MED_CHIP_COLOR,
  HIGH_CHIP_COLOR,
} from "../Scoring/scoreTheme";

/** Subtle breathing animation for severity pulse dots. */
const pulse = keyframes`
  0%   { transform: scale(0.95); opacity: 0.7; }
  50%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.7; }
`;

/** Map severity string to its theme color. */
const getSeverityDotColor = (
  severity: string | null | undefined,
): string | null => {
  if (!severity) return null;
  const s = severity.toLowerCase();
  if (s.includes("low")) return LOW_CHIP_COLOR;
  if (s.includes("medium") || s.includes("med")) return MED_CHIP_COLOR;
  if (s.includes("high")) return HIGH_CHIP_COLOR;
  return null;
};

const DetailedValidationResult = ({
  validateResult,
  isLoading,
  setIsLoading,
  isBackdropLoading,
  currentIncidentData,
  setIsTriggereBtnPressed,
  setIsCommentBtnPressed,
  currentResourceResultId,
}: IDetailedValidationResult) => {
  const { hasPermission } = useAuth();

  // Permission checks for validation result actions
  const canEditValidationIssues = hasPermission("validation_issues", "edit");

  const [rowsData, setRowsData] = useState<any>([]);
  const [columnsData, setColumnsData] = useState<any>([]);
  const [gridColumnLength, setGridColumnLength] = useState<number>(0);
  const [selectedRowID, setSelectedRowID] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("validationErrorsSummary");
  const [userDialogData, setUserDialogData] = useState<any>({
    status: false,
    dialogName: "",
    selectedIds: [],
  });
  const [allUsersList, setallUsersList] = useState<any>([]);
  const [selectedIds, setSelectedIds] = useState<any>([]);

  useEffect(() => {
    setUserDialogData((prev: any) => ({
      ...prev,
      selectedIds: selectedIds || [],
    }));
  }, [selectedIds]);

  const [usersList] = useFetchUsersList({
    setIsLoading,
  });

  useEffect(() => {
    setallUsersList(usersList);
  }, [usersList]);

  const getFormattedKeysPairs = (inputString: string) => {
    const pairs: any = {};
    inputString.split(",").forEach((pair) => {
      const [key, value] = pair.trim().split("=");
      pairs[key] = value;
    });
    return pairs;
  };

  const handelRowData = (validation_errors: any) => {
    const rows: any = [];
    validation_errors?.forEach((item: any, idx: any) => {
      const { key_columns, ...rest } = item ?? {};
      const row: any = {
        id: idx,
        ...getFormattedKeysPairs(item?.key_columns),
        ...rest,
      };
      rows.push(row);
    });
    setRowsData(rows);
  };

  const handelSummaryRowData = (validation_errors: any) => {
    const { key_columns, key_column_values } = validation_errors || {};
    if (key_column_values?.length > 0 && key_columns?.length > 0) {
      const rowsData = key_column_values.map((values: any, index: any) => {
        const rowObject: any = {};
        key_columns.forEach((column: any, columnIndex: any) => {
          rowObject[column] = values[columnIndex];
        });
        rowObject.id = index + 1;
        return rowObject;
      });
      setRowsData(rowsData);
    }
  };

  const getColumns = (validation_errors: any) => {
    if (validation_errors && validation_errors.length > 0) {
      const firstColumns: any = Object.keys(
        getFormattedKeysPairs(validation_errors[0]?.key_columns),
      );
      const secondColumns: any = Object.keys(validation_errors[0]).filter(
        (item) => item !== "key_columns" && item !== "file_names",
      );
      const columns: any = [
        {
          ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
          renderCell: (params: any) => {
            const customValidationKeys = Object.keys(params?.row).filter(
              (key) => key.includes("_custom_validation"),
            );
            const isCustomValidation = customValidationKeys.every(
              (key) =>
                params?.row.hasOwnProperty(key) &&
                (params?.row[key] === null || params?.row[key] === ""),
            );
            return (
              <>
                {!isCustomValidation && (
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    className="custom-validation-arrow"
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                )}
              </>
            );
          },
        },

        ...firstColumns.map((item: any) => {
          let newItem = item.replace(/^['"]|['"]$/g, "");
          setGridColumnLength(firstColumns.length);
          return {
            field: item,
            headerName: newItem.toUpperCase(),
            flex: 1,
            maxWidth: 160,
          };
        }),
        ...secondColumns.map((item: any) => {
          let newItem = item.replace(/^['"]|['"]$/g, "");
          return {
            field: item,
            headerName: newItem.toUpperCase(),
            flex: 1,
            renderCell: (params: any) => {
              return (
                <>
                  {params.value && params.value.length > 100 ? (
                    <ReadMoreLess data={params?.value?.replace(/"/g, "")} />
                  ) : (
                    <Box className="word-break-all">
                      {params?.value?.replace(/"/g, "")}
                    </Box>
                  )}
                </>
              );
            },
          };
        }),
        {
          field: "file_names",
          headerName: "FILE_NAME(S)",
          flex: 1,
          renderCell: (params: any) => (
            <div
              className="data-grid-cell"
              dangerouslySetInnerHTML={{ __html: params.value }}
            />
          ),
        },
      ];
      setColumnsData(columns);
    }
  };

  const getSummaryColumns = (validation_errors: any) => {
    const { key_columns } = validation_errors || {};
    if (key_columns?.length > 0) {
      const columns: any = key_columns.map((item: any) => {
        return {
          field: item,
          headerName: item.toUpperCase(),
          flex: 1,
          maxWidth: 160,
        };
      });
      setColumnsData([
        {
          ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
          renderCell: (params: any) => {
            const customValidationKeys = Object.keys(params?.row).filter(
              (key) => key.includes("_custom_validation"),
            );
            const isCustomValidation = customValidationKeys.every(
              (key) =>
                params?.row.hasOwnProperty(key) &&
                (params?.row[key] === null || params?.row[key] === ""),
            );
            return (
              <>
                {!isCustomValidation && (
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    className="custom-validation-arrow"
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                )}
              </>
            );
          },
        },
        ...columns,
      ]);
    }
  };

  const validationColumns: any = [
    {
      field: "__status",
      headerName: "",
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const dotColor = getSeverityDotColor(params?.row?.validation_severity);
        if (!dotColor) return null;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: dotColor,
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
            />
          </Box>
        );
      },
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params: any) => {
        const rowId = params?.row?.id;

        const handleClick = () => {
          if (selectedRowID === rowId) {
            setSelectedRowID(null);
          } else {
            setSelectedRowID(rowId);
          }
        };
        return (
          !validateResult?.additional_properties?.summary_mode && (
            <IconButton
              size="small"
              tabIndex={-1}
              className="custom-validation-arrow"
              onClick={handleClick}
            >
              <ExpandMoreIcon />
            </IconButton>
          )
        );
      },
    },
    {
      field: "column_name",
      headerName: "Column Name",
      flex: 1,
      maxWidth: 280,
    },
    {
      field: "total_validation_errors",
      headerName: "Total Validation Errors",
      flex: 1,
      maxWidth: 280,
      renderCell: (params: any) => {
        const value = params.value;
        return value !== undefined && value !== null
          ? formatNumberWithCommas(value)
          : "";
      },
    },
    {
      field: "total_general_validation_errors",
      headerName: "Total General Validation Errors",
      flex: 1,
      maxWidth: 280,
      valueGetter: (params: any) => {
        const dict = params?.row?.general_validation_errors;
        if (!dict) return 0;
        return Object.values(dict).reduce(
          (sum: number, v: any) => sum + (Number(v) || 0),
          0,
        );
      },
      renderCell: (params: any) => {
        const value = params.value;
        return value !== undefined && value !== null
          ? formatNumberWithCommas(value)
          : "";
      },
    },
    {
      field: "total_custom_validation_errors",
      headerName: "Total Custom Validation Errors",
      flex: 1,
      maxWidth: 280,
      valueGetter: (params: any) => {
        const arr = params?.row?.custom_validation_errros;
        if (!Array.isArray(arr) || arr.length === 0) return 0;
        return arr.reduce(
          (sum: number, v: any) =>
            sum + (Number(v?.total_validation_errors) || 0),
          0,
        );
      },
      renderCell: (params: any) => {
        const value = params.value;
        return value !== undefined && value !== null
          ? formatNumberWithCommas(value)
          : "";
      },
    },
    {
      field: "validation_severity",
      headerName: "Validation Severity",
      flex: 1,
      maxWidth: 280,
      renderCell: (params: any) => {
        const transformedValue = params.value
          .split("_")
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        return params.value;
      },
    },
  ];

  const validationRows: any =
    validateResult?.additional_properties?.validation_errors_summary?.map(
      (item: any, index: any) => {
        const sanitizedValidationErrors = item?.validation_errors?.map(
          (error: any) => {
            const newArr =
              error?.file_names &&
              error?.file_names?.replace(/'/g, "").split(",");
            return {
              ...error,
              file_names:
                newArr && newArr.length > 1
                  ? `<ul class="ul-inside-grid">${newArr
                      .map((arr: any) => `<li>${arr}</li>`)
                      .join("")}</ul>`
                  : `<div class="word-break-all">${
                      (newArr && newArr[0]) || ""
                    }<div>`,
            };
          },
        );

        return {
          id: index,
          ...item,
          validation_errors: sanitizedValidationErrors,
        };
      },
    );

  useEffect(() => {
    const validationErrorsData =
      validateResult?.additional_properties?.validation_errors_summary?.[
        selectedRowID
      ]?.validation_errors;

    if (validationErrorsData !== null) {
      if (validateResult?.additional_properties?.summary_mode) {
        getSummaryColumns(validationErrorsData);
        handelSummaryRowData(validationErrorsData);
      } else {
        getColumns(validationErrorsData);
        handelRowData(validationErrorsData);
      }
    }
  }, [validateResult, selectedRowID]);
  const {
    resource_name,
    file_name,
    linked_service_code,
    connection_key,
    resource_path,
    resource_id,
    ...otherProps
  } = validateResult?.validation_parameters || {};

  const tabContent: any = {
    validationErrorsSummary: (
      <>
        {validationRows?.length > 0 ? (
          <ValidationSummaryDataGrid
            dataColumns={validationColumns || []}
            dataRows={validationRows || []}
            loading={isLoading}
            setLoading={setIsLoading}
            isExportButtonRequired={true}
            className="dataTable no-radius"
            rowCount={rowsData?.total}
            pageSizeOptions={[25, 500]}
            historyType={"Validation"}
            disableColumnFilter={false}
            validationErrorProps={{
              dataRows: rowsData,
              dataColumns: columnsData,
              loading: isLoading,
              className: "dataTable no-radius",
              gridColumnLength: gridColumnLength,
              dataListTitle: "",
              isHeight100: true,
              isIssuesDataAvailable:
                validateResult?.is_issues_data_available || false,
            }}
          />
        ) : (
          <Box
            sx={{
              paddingTop: "24px",
              paddingBottom: "24px",
              display: "flex",
              justifyContent: "center",
            }}
            className="adhock-query-accordion-wrapper"
          >
            No data available
          </Box>
        )}
      </>
    ),
    crossFieldValidations: (
      <Box className="adhock-query-accordion-wrapper1">
        <AdhocQueryDashboardTab
          validateResult={validateResult}
          adhocQueryData={
            validateResult?.additional_properties
              ?.cross_field_validation_results || []
          }
        />
      </Box>
    ),
    duplicateRecords: (
      <Box>
        <DuplicateRecords
          validateResult={validateResult}
          userDialogData={userDialogData}
          setUserDialogData={setUserDialogData}
          allUsersList={allUsersList}
        />
      </Box>
    ),
    nullKeyErrors: (
      <Box>
        <NullKeyErrors
          validateResult={validateResult}
          userDialogData={userDialogData}
          setUserDialogData={setUserDialogData}
          allUsersList={allUsersList}
        />
      </Box>
    ),
  };
  const handleChangeTab = (event: any, newValue: any) => {
    setActiveTab(newValue);
    setSelectedRowID(null);
  };

  return (
    <>
      <Loader isLoading={isBackdropLoading} />
      <div className="validate-page">
        {/* Header */}
        <ValidationResultHeader validateResult={validateResult} />

        {/* Resource Statistics */}
        <ValidationResultResourceStatistics validateResult={validateResult} />

        {/* Incidents */}
        <Incidents
          currentResultId={currentResourceResultId || ""}
          currentIncidentData={currentIncidentData ?? []}
          setIsLoading={setIsLoading}
          setIsTriggereBtnPressed={setIsTriggereBtnPressed}
          setIsCommentBtnPressed={setIsCommentBtnPressed}
        />

        {/* Validation Parameters */}
        {validateResult?.complete_validation_params
          ?.validation_request_body && (
          <ValidationResultParameters validateResult={validateResult} />
        )}

        {/* Supporting Documents */}
        {validateResult?.additional_properties?.supporting_documents && (
          <div className="accordion-panel mt-8">
            <ResourceSupportDocuments
              title="Supporting Documents"
              documents={
                validateResult?.additional_properties?.supporting_documents
              }
            />
          </div>
        )}

        {/* Resource Variables */}
        {validateResult?.complete_validation_params?.validation_request_body
          ?.inline_variables && (
          <ValidationResultVariables
            resourceVariables={
              validateResult?.complete_validation_params
                ?.validation_request_body?.inline_variables
            }
          />
        )}
        <Grid container className="tabs-with-butttons">
          <Grid item xs>
            <Tabs
              sx={{
                mt: 1,
              }}
              value={activeTab}
              onChange={handleChangeTab}
              className="mui-tabs alternative-1"
              variant="scrollable"
            >
              <Tab
                label="Validation Errors Summary"
                value="validationErrorsSummary"
              />
              <Tab label="Adhoc Query" value="crossFieldValidations" />
              <Tab label="Duplicate Records" value="duplicateRecords" />
              <Tab label="Null Key Errors" value="nullKeyErrors" />
            </Tabs>
          </Grid>
          {canEditValidationIssues &&
            validateResult?.is_issues_data_available &&
            activeTab !== "crossFieldValidations" &&
            (activeTab !== "nullKeyErrors" ||
              userDialogData?.hasNullKeyErrorsData) &&
            (activeTab !== "duplicateRecords" ||
              userDialogData?.hasDuplicateRecordsData) && (
              <Grid item className="buttons-group">
                <Button
                  className="btn-blue btn-orange btn-sm"
                  onClick={() => {
                    setUserDialogData((prev: any) => ({
                      ...prev,
                      status: true,
                      dialogName: "Update Issue",
                    }));
                  }}
                  disabled={userDialogData?.selectedIds?.length === 0}
                >
                  Update Issue
                </Button>
              </Grid>
            )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <Box>{tabContent[activeTab]}</Box>
        </Grid>
      </div>
    </>
  );
};

export default DetailedValidationResult;
